import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Check, Circle } from 'lucide-react';
import { rankOptionsByQuery } from '../../utils/optionSearch';
import './SelectField.css';

/**
 * SelectSheet - Keyboard-stable Bottom sheet modal for SelectField
 * Optimized for mobile with RTL support and visual viewport handling
 */
export const SelectSheet = ({
  open,
  onClose,
  title,
  options = [],
  value,
  onSelect,
  searchable = false,
  getLabel,
  getIcon,
  rtl = false,
  multiSelect = false,
  testId = 'select-sheet'
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const searchInputRef = useRef(null);
  const sheetRef = useRef(null);
  const listRef = useRef(null);
  
  // Filter + RANK options based on search.
  // Uses the shared `rankOptionsByQuery` utility so:
  //   - EN, AR, and `aliases` are all matched together
  //   - typing "mobile" puts **Mobile & Accessories** above **Electronics**
  //     (the former has "mobile" in its visible label → higher score; the
  //     latter only has it as an alias)
  //   - typing "laptop" puts **Laptops & Accessories** above **Electronics**
  //   - direct matches never get hidden below broad parents.
  // We pass `getLabel` as an extra getter so any caller-derived label
  // also contributes to the LABEL tier of the score.
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return options;
    }
    const extraGetters = getLabel ? [getLabel] : [];
    return rankOptionsByQuery(options, searchQuery, extraGetters);
  }, [options, searchQuery, searchable, getLabel]);
  
  // Reset search when sheet closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setIsClosing(false);
      setKeyboardHeight(0);
    }
  }, [open]);
  
  // Handle keyboard via visualViewport API (best for mobile web)
  useEffect(() => {
    if (!open) return;
    
    const vv = window.visualViewport;
    if (!vv) return;
    
    const onResize = () => {
      // When keyboard opens, visualViewport.height shrinks
      // Calculate approximate keyboard height
      const kbHeight = Math.max(
        0,
        window.innerHeight - vv.height - (vv.offsetTop || 0)
      );
      setKeyboardHeight(kbHeight);
      
      // Ensure the sheet scrolls to show focused input
      if (kbHeight > 0 && listRef.current) {
        // Scroll the list slightly to accommodate keyboard
        listRef.current.scrollTop = listRef.current.scrollTop;
      }
    };
    
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    onResize();
    
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, [open]);
  
  // Lock body scroll when sheet is open
  useEffect(() => {
    if (!open) return;
    
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const scrollY = window.scrollY;
    
    // Prevent body scroll and layout jumping
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.width = '';
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [open]);
  
  // Focus search input when sheet opens (without scrolling page)
  useEffect(() => {
    if (open && searchable && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [open, searchable]);
  
  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, []);
  
  // Animated close
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 150);
  }, [onClose]);
  
  // Check if option is selected
  const isSelected = useCallback((optValue) => {
    if (multiSelect) {
      return Array.isArray(value) && value.includes(optValue);
    }
    return value === optValue;
  }, [value, multiSelect]);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleClose]);
  
  if (!open) return null;
  
  // Calculate dynamic padding for keyboard
  const dynamicPaddingBottom = keyboardHeight > 0 ? keyboardHeight + 16 : 16;
  
  return (
    <div 
      className={`wassla-sheet-overlay ${isClosing ? 'is-closing' : ''}`}
      onClick={handleBackdropClick}
      data-testid={`${testId}-overlay`}
      style={{
        overscrollBehavior: 'contain',
      }}
    >
      <div 
        ref={sheetRef}
        className={`wassla-sheet ${isClosing ? 'is-closing' : ''}`}
        dir={rtl ? 'rtl' : 'ltr'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sheet-title"
        data-testid={`${testId}-panel`}
        style={{
          paddingBottom: dynamicPaddingBottom,
          maxHeight: keyboardHeight > 0 ? `calc(100vh - ${keyboardHeight}px)` : '85vh',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          transform: 'translateZ(0)',
        }}
      >
        {/* Handle */}
        <div className="wassla-sheet-handle-area">
          <div className="wassla-sheet-handle" />
        </div>
        
        {/* Header */}
        <div className="wassla-sheet-header">
          <h2 id="sheet-title" className="wassla-sheet-title">
            {title}
          </h2>
          <button
            type="button"
            className="wassla-sheet-close"
            onClick={handleClose}
            aria-label={rtl ? 'إغلاق' : 'Close'}
            data-testid={`${testId}-close`}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Search */}
        {searchable && (
          <div className="wassla-sheet-search">
            <Search size={18} className="wassla-sheet-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              className="wassla-sheet-search-input"
              placeholder={rtl ? 'بحث…' : 'Search…'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              dir={rtl ? 'rtl' : 'ltr'}
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-testid={`${testId}-search`}
            />
          </div>
        )}
        
        {/* Options List */}
        <div 
          ref={listRef}
          className="wassla-sheet-list" 
          role="listbox"
          style={{
            overscrollBehavior: 'contain',
          }}
        >
          {filteredOptions.length === 0 ? (
            <div className="wassla-sheet-empty">
              {rtl ? 'لا توجد نتائج' : 'No results'}
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const selected = isSelected(option.value);
              const label = getLabel ? getLabel(option) : option.label;
              const icon = getIcon ? getIcon(option) : option.icon;
              
              return (
                <button
                  key={option.value || index}
                  type="button"
                  className={`wassla-sheet-option ${selected ? 'is-selected' : ''}`}
                  onClick={() => onSelect(option.value)}
                  role="option"
                  aria-selected={selected}
                  data-testid={`${testId}-option-${option.value}`}
                >
                  {icon && (
                    <span className="wassla-sheet-option-icon">
                      {icon}
                    </span>
                  )}
                  
                  <span className="wassla-sheet-option-label">
                    {label}
                  </span>
                  
                  <span className="wassla-sheet-option-indicator">
                    {selected ? (
                      <Check size={18} className="check-icon" />
                    ) : (
                      <Circle size={18} className="radio-icon" />
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
        
        {/* Multi-select Apply button */}
        {multiSelect && (
          <div className="wassla-sheet-footer">
            <button
              type="button"
              className="wassla-sheet-apply"
              onClick={handleClose}
              data-testid={`${testId}-apply`}
            >
              {rtl ? 'تطبيق' : 'Apply'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectSheet;
